import React, { useMemo, useEffect, useState, useRef } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  LabelList
} from "recharts";

import { tableFromIPC } from "apache-arrow";
import { getPolConfig } from "./polAggregators";
import { schemaRequestFields } from "./schemaTypes";
import { DEFAULT_WEIGHT_COL, weightForRow, weightFromRowByCol } from "./keyWeights";
import { geoMercator, geoPath } from "d3-geo";
import { geoAssetUrl } from "./geoAssets";
import { Box, Paper, Stack, Grid, Divider, Typography, Chip, useTheme } from "@mui/material";

import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";

import WorkIcon from "@mui/icons-material/Work";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SchoolIcon from "@mui/icons-material/School";
import HomeIcon from "@mui/icons-material/Home";
import PublicIcon from "@mui/icons-material/Public";
import GavelIcon from "@mui/icons-material/Gavel";
import ParkIcon from "@mui/icons-material/Park";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import TheatersIcon from "@mui/icons-material/Theaters";

import ElderlyRoundedIcon from "@mui/icons-material/ElderlyRounded";
import FmdBadRoundedIcon from "@mui/icons-material/FmdBadRounded";
import PeopleIcon from "@mui/icons-material/People";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";

import { green, red, blue, grey, amber, purple } from "@mui/material/colors";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import { Avatar } from "@mui/material";
import { alpha } from "@mui/material/styles";

/* -------------------------------------------------------------------------- */
/* CONFIG                                                                     */
/* -------------------------------------------------------------------------- */

const PARTY_COLOURS = {
  Conservative: "#0B5ED7",
  "Conservative Party": "#0B5ED7",
  Labour: "#E4003B",
  "Labour Party": "#E4003B",
  "Labour Co-operative": "#E4003B",
  "Liberal Democrat": "#F6A800",
  "Lib Dem": "#F6A800",
  "Liberal Democrats": "#F6A800",
  Reform: "#12B6CF",
  "Reform UK": "#12B6CF",
  Green: "#2B8A3E",
  "Green Party": "#2B8A3E",
  SNP: "#FBDD23",
  "Scottish National Party": "#FBDD23",
  Plaid: "#3F9D37",
  "Plaid Cymru": "#3F9D37",
  Independent: "#6B7280",
  Other: "#6B7280",
};


const normalizeParty = (raw = "") => {
  const s = String(raw).trim();
  if (/conservative/i.test(s)) return "Conservative";
  if (/labour/i.test(s)) return "Labour";
  if (/lib(eral)?\s*dem/i.test(s)) return "Lib Dem";
  if (/reform/i.test(s)) return "Reform";
  if (/green/i.test(s)) return "Green";
  if (/scottish\s*national|snp/i.test(s)) return "SNP";
  if (/plaid/i.test(s)) return "Plaid";
  if (/independent/i.test(s)) return "Independent";
  return "Other";
};

const partyColor = (name) => PARTY_COLOURS[normalizeParty(name)] || PARTY_COLOURS.Other;
const abbrev = (p) =>
  ({ Labour: "Labour",
    Conservative: "Conservatives", "Liberal Democrat": "LD", Green: "Green", SNP: "SNP", Plaid: "PC", Reform: "Reform", 
   
    Independent: "IND", 
    Other: "OTH" }[normalizeParty(p)] ||
    normalizeParty(p));

// Singular party names: these strings are looked up directly against the party
// colour maps in the report views, and "Conservatives" missed every one of them
// (silently rendering a grey pill for a Conservative seat).
const party2024lookup = {
     Lab: "Labour",
    Con: "Conservative",
    RUK: "Reform",
    Ind: "Independent", 
    SNP: "SNP", 
    Green: "Green",
    LD:"Liberal Democrat",
    PC:"Plaid Cymru"

}

const clampAlpha = (hex, a = 0.22) => alpha(hex || "#94A3B8", a); 
const printBorder = (hex) => `1px solid ${alpha(hex || "#64748B", 0.85)}`;
const pct = (x) => (Number.isFinite(x) ? `${x.toFixed(1)}%` : "—");
const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString() : "—");

const CSV_URL = process.env.PUBLIC_URL + "/static/data/Notional2019Winners_Newpcon.csv";
const emails_2024_CSV_URL = process.env.PUBLIC_URL + "/static/data/251118_2024_results_and_emails.csv";

export function parseLatestElection(latestElection) {
  if (!latestElection || !latestElection.value) {
    return { year: 2024, party: null, majority: null, share: null };
  }
  const v = latestElection.value;
  const party = v.winningParty?.name || null;

  let share = null;
  if (Array.isArray(v.candidates) && v.candidates.length) {
    const byRank = v.candidates.find((c) => c.rankOrder === 1);
    const byParty = v.candidates.find((c) => c.party?.name === party);
    const win = byRank || byParty || null;
    if (win && typeof win.voteShare === "number") {
      // Some APIs use 0–1, others 0–100. Normalize gently.
      share = win.voteShare <= 1 ? win.voteShare * 100 : win.voteShare;
    }
  }

  return {
    year: 2024,
    party,
    majority: Number.isFinite(v.majority) ? v.majority : null,
    share: Number.isFinite(share) ? share : null,
  };
}
function parseCSVLine(line) {
  const cols = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      // Handle escaped quotes ("") inside a quoted field
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      cols.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  cols.push(cur); // push final field
  return cols;
}
export async function fetchLatestElection(constCode) {
  if (!constCode) return null;
  // Use 'await' to wait for the fetch call to complete and get the response object
  const res = await fetch(emails_2024_CSV_URL);

  if (!res.ok) {
    // Throw an error or return null if the fetch failed
    throw new Error(`emails_2024_CSV_URL fetch failed: ${res.status}`);
  }

  // Use 'await' to wait for the response body to be read as text
  const text = await res.text();


  // The rest of your synchronous parsing logic remains the same
  const lines = text.trim().split("\n");
  if (lines.length < 2) return null;

  const rawHeader = lines[0];
  const safeHeader = rawHeader.replace(/^\uFEFF/, "");
    const header = parseCSVLine(safeHeader);
  const rows = lines.slice(1);

  for (const line of rows) {
    const cols = parseCSVLine(line);  // <-- here
    const row = Object.fromEntries(header.map((k, i) => [k.trim(), cols[i]?.trim()]));
    if (row.new_pcon === constCode) {
      console.log("row", row);

      const party = party2024lookup[row.winner] || "Other";
      const majority = Number(row?.Majority);
      const share = Number(row?.winning_perc);

      return {
        year: 2024,
        party,
        majority: Number.isFinite(majority) ? majority : null,
        // A number at full precision, not a rounded string. `.toFixed(0)`
        // both lost half a point (Barking's 44.5% printed as 44.0% once the
        // pill re-formatted it to one decimal) and returned a string, which
        // SlimTag's `Number.isFinite(share)` guard rejected outright — so the
        // MP card showed no 2024 share at all. Every consumer formats it.
        share: Number.isFinite(share) ? share * 100 : null,
      };
    }
  }
  return null;
}


async function fetch2019Winner(constCode) {
  if (!constCode) return null;

  const text = await fetch(CSV_URL).then((res) => {
    if (!res.ok) throw new Error(`CSV fetch failed: ${res.status}`);
    return res.text();
  });

  const lines = text.trim().split("\n");
  if (lines.length < 2) return null;

  const rawHeader = lines[0];
  const safeHeader = rawHeader.replace(/^\uFEFF/, "");
  const header = safeHeader.split(",");
  const rows = lines.slice(1);

  for (const line of rows) {
    const cols = line.split(",");
    const row = Object.fromEntries(header.map((k, i) => [k.trim(), cols[i]?.trim()]));
    if (row.new_pcon === constCode) {
      const shares = {
        Conservative: Number(row.con19),
        Labour: Number(row.lab19),
        "Liberal Democrat": Number(row.ld19),
        Green: Number(row.green19),
        Reform: Number(row.brx19), 
        Plaid: Number(row.pc19),
        SNP: Number(row.snp19),
        Other: Number(row.other19),
      };
      const party = row.winner || Object.entries(shares).reduce((a, b) => (b[1] > (shares[a] || 0) ? b[0] : a), "Other");
      // normalizeParty returns "Lib Dem", which is not a key here — every
      // Lib Dem seat's 2019 share came back 0. Look the share up by the CSV's
      // own column instead, which needs no name to agree with any other name.
      const SHARE_COL = {
        Conservative: "con19", Labour: "lab19", "Lib Dem": "ld19", Green: "green19",
        Reform: "brx19", Plaid: "pc19", SNP: "snp19", Other: "other19",
      };
      const share0to1 = Number(row[SHARE_COL[normalizeParty(party)]]) || 0;
      return {
        year: 2019,
        party,
        share: share0to1 * 100,
      };
    }
  }
  return null;
}


function OutcomePill({ color, label, avatarSrc }) {
  return (
    <Chip
      avatar={
        avatarSrc ? (
          <Avatar
            src={avatarSrc}
            alt=""
            imgProps={{ style: { objectFit: "contain" } }}
            sx={{ width: 16, height: 16, bgcolor: "transparent" }}
          />
        ) : (
          <Avatar sx={{ width: 16, height: 16, bgcolor: color, color: "#0B0F19", fontSize: 12 }} />
        )
      }
      label={label}
      size="small"
      variant="outlined"
      sx={{
        pl: 0.25,
        pr: 0.6,
        border: printBorder(color),
        background: clampAlpha(color, 0.22),
        "& .MuiChip-label": { fontWeight: 800, letterSpacing: 0.2 },
      }}
    />
  );
}

function Pill2024({ party, share, majority }) {
  const color = partyColor(party);
  const bits = [
    "2024",
    party ? abbrev(party) : "—",
    Number.isFinite(share) ? pct(share) : null,
    Number.isFinite(majority) ? `Maj ${fmt(majority)}` : null,
  ].filter(Boolean);
  return <OutcomePill color={color} label={bits.join(" · ")} />;
}

function PillModel({ topVI, logoUrl }) {
  if (!topVI) return <OutcomePill color="#94A3B8" label="Model · VI —" avatarSrc={logoUrl} />;
  const color = topVI.color || partyColor(topVI.label);
  const label = `Model · VI ${abbrev(topVI.label)} ${pct(Number(topVI.constituency))}`;
  return <OutcomePill color={color} label={label} avatarSrc={logoUrl} />;
}

function Pill2019({ party, share }) {
  const color = partyColor(party);
  const bits = ["2019", party ? abbrev(party) : "—", Number.isFinite(share) ? pct(share) : null].filter(Boolean);
  return <OutcomePill color={color} label={bits.join(" · ")} />;
}


const toPercentString = (v) => {
  if (!Number.isFinite(v)) return "—";
  const pct = v <= 1 ? v * 100 : v; 
  return `${pct.toFixed(1)}%`;
};

function extract2024Share(latestElection) {
  const v = latestElection?.value;
  if (!v || !Array.isArray(v.candidates)) return null;
  const winnerByRank = v.candidates.find((c) => c.rankOrder === 1);
  if (Number.isFinite(winnerByRank?.voteShare)) return winnerByRank.voteShare;
  const wName = v.winningParty?.name || v.party?.name;
  const match = v.candidates.find((c) =>
    (c.party?.name || "").toLowerCase() === (wName || "").toLowerCase()
  );
  return Number.isFinite(match?.voteShare) ? match.voteShare : null;
}

function shareKeyFor2019(partyName) {
  const p = (normalizeParty(partyName) || "").toLowerCase();
  if (p.includes("conservative")) return "con";
  if (p.includes("labour")) return "lab";
  if (p.includes("liberal")) return "ld";
  if (p.includes("green")) return "green";
  if (p === "snp") return "snp";
  if (p === "plaid") return "pc";
  if (p.includes("reform")) return "brx"; 
  if (p.includes("independent")) return "other";
  return "other";
}

function get2019Share(row) {
  if (!row?.shares) return null;
  const key = shareKeyFor2019(row.party);
  const val = row.shares[key];
  return Number.isFinite(val) ? val : null;
}


function Pill({ label, party, share, majority, logoUrl }) {
  const color = partyColor(party);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        py: 0.6,
        border: `1px solid ${color}`,
        borderRadius: 999,
        background: `${color}1A`, // ~10% tint
        flexShrink: 0,
      }}
    >
      {logoUrl ? (
        <Box
          component="img"
          src={logoUrl}
          alt=""
          sx={{ height: 16, width: 16, objectFit: "contain", display: "block" }}
        />
      ) : (
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
      )}

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, flexWrap: "nowrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 800, color: "#0B0F19" }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: "#0B0F19" }}>
          {normalizeParty(party)}
        </Typography>
        {share != null && (
          <Typography variant="body2" sx={{ color: "#0B0F19" }}>
            ({toPercentString(share)})
          </Typography>
        )}
        {Number.isFinite(majority) && (
          <>
            <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "rgba(15,23,42,0.6)" }} />
            <Typography variant="body2" sx={{ color: "#334155" }}>
              Majority {majority.toLocaleString()}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}


const Dot = ({ color, size = 6 }) => (
  <Box sx={{ width: size, height: size, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
);

export function SlimTag({ label, party, share, majority, logoUrl }) {
  const partyNameRaw = normalizeParty(party);
  const partyName = partyNameRaw === "Liberal Democrat" ? "LibDem" : partyNameRaw;
  const baseColor = partyColor(partyNameRaw) || "#999";


  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.6,
        borderRadius: "999px", // Full pill shape
        bgcolor: alpha(baseColor, 0.08), // Very sleek, light tint
        border: `1px solid ${alpha(baseColor, 0.15)}`, // Barely visible border for definition
        transition: "all 0.2s ease",
        // Prevent compression
        flexShrink: 0, 
        maxWidth: "100%",
        flexGrow: 0
      }}
    >
      {/* Visual Indicator: Logo or Dot */}
      {label === "Latest Projection" && logoUrl ? (
        <Box
          component="img"
          src={logoUrl}
          alt=""
          sx={{ height: 14, width: "auto", opacity: 0.9 }}
        />
      ) : (
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: baseColor,
            boxShadow: `0 0 0 2px ${alpha(baseColor, 0.3)}`, // Glow effect
          }}
        />
      )}

      {/* Text Container */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6, flexWrap: "wrap" }}>
        {/* The Label (e.g. "2019 Result:") */}
        <Typography
          component="span"
          sx={{
            // Responsive font size: shrinks slightly on mobile, caps at 0.7rem
            fontSize: "clamp(0.65rem, 1vw, 0.7rem)", 
            fontWeight: 700,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </Typography>

        {/* The Party Name */}
        <Typography
          component="span"
          sx={{
            fontSize: "clamp(0.75rem, 1.1vw, 0.8rem)",
            fontWeight: 800,
            color: "text.primary",
          }}
        >
          {partyName ?? "—"}
        </Typography>

        {/* Stats Group */}
        {(Number.isFinite(share) || Number.isFinite(majority)) && (
          <Box component="span" sx={{ display: "inline-flex", gap: 0.5, alignItems: "baseline" }}>
            {Number.isFinite(share) && (
              <Typography
                component="span"
                sx={{
                  fontSize: "clamp(0.7rem, 1vw, 0.75rem)",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                {share.toFixed(1)}%
              </Typography>
            )}

            {Number.isFinite(majority) && (
              <>
                <Typography component="span" sx={{ color: "text.disabled", fontSize: "0.7rem" }}>
                  •
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: "clamp(0.7rem, 1vw, 0.75rem)",
                    color: "text.secondary",
                  }}
                >
                  Maj {majority.toLocaleString()}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function TopHeader({ constName, areaCode, mpData, topVI, badgeLabel = null }) {
  const theme = useTheme();
  const [winner2019, setWinner2019] = useState(null);
  const [latestElection, setLatestElection] = useState(null);

  // --- Data Fetching ---
  useEffect(() => {
    let cancelled = false;
    setLatestElection(null);
    async function getLatestElection() {
      try {
        const data = await fetchLatestElection(areaCode);
        if (!cancelled) setLatestElection(data);
      } catch (error) {
        console.error(error);
        if (!cancelled) setLatestElection(null);
      }
    }
    getLatestElection();
    return () => { cancelled = true; };
  }, [areaCode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const row = await fetch2019Winner(areaCode);
      if (!cancelled) setWinner2019(row);
    })();
    return () => (cancelled = true);
  }, [areaCode]);

  // --- Derived Values ---
  const winner2024Party = latestElection?.party;
  const winner2024Maj = latestElection?.majority;
  const winner2024Share = latestElection?.share ?? extract2024Share(mpData?.latestElection);

  const projParty = topVI?.label ?? null;
  const projShare = Number.isFinite(+topVI?.constituency) ? +topVI.constituency : undefined;

  const winner2019Party = winner2019?.party ?? null;
  const winner2019Share = Number.isFinite(winner2019?.share) ? winner2019.share : undefined;

  const accent = winner2024Party
    ? partyColor(winner2024Party)
    : projParty
    ? partyColor(projParty)
    : "#5FE1E6";

  const stackLogo = process.env.PUBLIC_URL + "/static/images/stack_logo.png";

  return (
    <Box sx={{ mb: 1.2 }}>
      {/* Top Row: Brand & Meta */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            component="img"
            src={stackLogo}
            alt="Stack Data Strategy"
            sx={{ height: 20, opacity: 0.8, filter: "grayscale(10%)" }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                letterSpacing: 1.2,
                fontSize: "0.7rem",
                lineHeight: 1,
              }}
            >
              Constituency Profile
            </Typography>
            {badgeLabel ? (
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  border: "1px solid #c7d2fe",
                  color: "#1d4ed8",
                  backgroundColor: "rgba(29,78,216,0.08)",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                {badgeLabel}
              </Box>
            ) : null}
          </Stack>
        </Stack>

        {/* Hide on very small screens to save space for data */}
        <Typography
          variant="caption"
          sx={{
            color: "text.disabled",
            display: { xs: "none", sm: "block" },
          }}
        >
          Prepared by Stack Data Strategy
        </Typography>
      </Stack>

      {/* Name + pills on one line + accent bar */}
      <Box sx={{ mb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "nowrap", mb: 0.5 }}>
          <Box
            component="span"
            sx={{
              fontWeight: 800,
              letterSpacing: -0.5,
              lineHeight: 1,
              color: "#1a202c",
              fontSize: "1.75rem",
              whiteSpace: "nowrap",
              flexShrink: 0,
              display: "block",
            }}
          >
            {constName || "Constituency Name"}
          </Box>

          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "nowrap", minWidth: 0 }}>
            <SlimTag label="Latest Projection" party={projParty} share={projShare} logoUrl={stackLogo} />
            <SlimTag label="2024 Result" party={winner2024Party} share={winner2024Share} majority={winner2024Maj} />
            <SlimTag label="2019 Result" party={winner2019Party} share={winner2019Share} />
          </Stack>
        </Stack>

        <Box
          sx={{
            height: 6,
            width: "clamp(60px, 30%, 180px)",
            borderRadius: 4,
            background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.3)})`,
          }}
        />
      </Box>
    </Box>
  );
}
export const MidSeparator = () => (
  <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "rgba(15,23,42,0.55)" }} />
);


// export function TopHeader({ constName, areaCode, mpData, topVI }) {
//   const [winner2019, setWinner2019] = useState(null);
//   const [latestElection, setLatestElection] = useState(null);

//   useEffect(() => {
//     let cancelled = false; // Cleanup flag
//     setLatestElection(null); // Reset state while fetching new data

//     async function getLatestElection() {
//       try {
//         const data = await fetchLatestElection(areaCode);
//         if (!cancelled) {
//           setLatestElection(data);
//         }
//       } catch (error) {
//         console.error("Failed to fetch latest election data:", error);
//         if (!cancelled) {
//             setLatestElection(null); // Set to null on error
//         }
//       }
//     }

//     getLatestElection();

//     return () => {
//       cancelled = true; // Cleanup function to prevent state update on unmounted component
//     };
//   }, [areaCode]); // Re-run effect when areaCode changes



//  const winner2024Party = latestElection?.party;
//  console.log("winner2024Party",winner2024Party)
//   const winner2024Maj = latestElection?.majority;
//   const winner2024Share = latestElection?.share ?? extract2024Share(mpData?.latestElection);
//   useEffect(() => {  
//     let cancelled = false;
//     (async () => {
//       const row = await fetch2019Winner(areaCode);
//       if (!cancelled) setWinner2019(row);
//     })();
//     return () => (cancelled = true);
//   }, [areaCode]);

//   const projParty = topVI?.label ?? null;
//   const projShare = Number.isFinite(+topVI?.constituency) ? +topVI.constituency : undefined;
  
//   const winner2019Party  = winner2019?.party ?? null;
//   const winner2019Share  = Number.isFinite(winner2019?.share) ? winner2019.share : undefined;

//   const accent = winner2024Party
//     ? partyColor(winner2024Party)
//     : projParty
//     ? partyColor(projParty)
//     : "#5FE1E6";

//   const stackLogo = process.env.PUBLIC_URL + "/static/images/stack_logo.png";


//   return (
//     <Box sx={{ mb:1 }}>

//       <Stack
//         direction="row"
//         alignItems="center"
//         justifyContent="space-between"
//         sx={{ mb: 1 }}
//       >
//         <Stack direction="row" alignItems="center" spacing={1.25}>
//           <Box
//             component="img"
//             src={stackLogo}
//             alt="Stack Data Strategy"
//             sx={{ height: 24 }}
//           />
//           <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 1 }}>
//             Constituency Profile
//           </Typography>
//         </Stack>
//         <Typography variant="caption" sx={{ color: "grey.700" }}>
//           Prepared by Stack Data Strategy
//         </Typography>
//       </Stack>

//       <Box sx={{ mb: 1 }}>
//         <Typography
//           variant="h4"
//           sx={{
//             fontWeight: 900,
//             letterSpacing: 0.3,
//             lineHeight: 1.1,
//             whiteSpace: "nowrap",
//             overflow: "hidden",
//             textOverflow: "ellipsis",
//             mb: 0.5
//           }}
//           title={constName}
//         >
//           {constName || "Constituency Name"}
//         </Typography>

//         <Box
//           sx={{
//             height: 4,
//             width: 260,
//             borderRadius: 999,
//             background: `linear-gradient(90deg, ${accent}, ${accent}55)`
//           }}
//         />
//       </Box>
//       <Box
//   sx={{
//     display: "flex",
//     alignItems: "center",
//     gap: 0.75,
//     flexWrap: "nowrap",   // <-- forces one row
//     mt: 0.5,
//     minWidth: 0,          // enables child ellipsis
//   }}
// >
//   <SlimTag
//     label="Latest Projection"
//     party={projParty}
//     share={projShare}
//     logoUrl={stackLogo}
//   />

//   <MidSeparator />

//   <SlimTag
//     label="2024 Result"
//     party={winner2024Party}
//     share={winner2024Share}
//     majority={winner2024Maj}
//   />

//   <MidSeparator />

//   <SlimTag
//     label="2019 Result"
//     party={winner2019Party}
//     share={winner2019Share}
//   />
// </Box>



//     </Box>
//   );
// }



export const ICON_MAP = {
  "Keir Starmer": (
    <img
      src={process.env.PUBLIC_URL + "/static/images/keir.png"}
      alt="Keir Starmer"
      style={{
        width: 60,
        height: 60,
        borderRadius: "50%",
        objectFit: "cover",
      }}
    />
  ),
  "Kemi Badenoch": (
    <img
      src={process.env.PUBLIC_URL + "/static/images/kemi.png"}
      alt="Kemi Badenoch"
      style={{
        width: 60,
        height: 60,
        borderRadius: "50%",
        objectFit: "cover",
      }}
    />
  ),
  "Ed Davey": (
    <img
      src={process.env.PUBLIC_URL + "/static/images/ed.png"}
      alt="Ed Davey"
      style={{
        width: 60,
        height: 60,
        borderRadius: "50%",
        objectFit: "cover",
      }}
    />
  ),
  "Nigel Farage": (
    <img
      src={process.env.PUBLIC_URL + "/static/images/nigel.png"}
      alt="Nigel Farage"
      style={{
        width: 60,
        height: 60,
        borderRadius: "50%",
        objectFit: "cover",
      }}
    />
  ),
  "Andy Burnham": (
    <img
      src={process.env.PUBLIC_URL + "/static/images/AndyBurnham.png"}
      alt="Andy Burnham"
      style={{
        width: 60,
        height: 60,
        borderRadius: "50%",
        objectFit: "cover",
      }}
    />
  ),
  "Don't know": (
    <img
      src={process.env.PUBLIC_URL + "/static/images/pm/dk.png"}
      alt="Don't know"
      style={{
        width: 60,
        height: 60,
        borderRadius: "50%",
        opacity: 0.7,
      }}
    />
  ),
  "None of these": (
    <img
      src={process.env.PUBLIC_URL + "/static/images/noone.png"}
      alt="None of these"
      style={{
        width: 60,
        height: 60,
        borderRadius: "50%",
        opacity: 0.7,
      }}
    />
  ),
};


export const ICON_MAP_PRIORITIES = {

  "Employment opportunities": <WorkIcon fontSize="small" />,          
  "Entertainment & leisure": <TheatersIcon fontSize="small" />,   
 "Environmental quality": <ParkIcon fontSize="small" />,        
  "Public facilities": <RoomServiceIcon fontSize="small" />,  
  "Affordable housing": <HomeIcon fontSize="small" />,            
  "Safety / crime reduction": <GavelIcon fontSize="small" />,             
  "Local services": <LocalHospitalIcon fontSize="small" />,   
  "Transport links": <DirectionsBusIcon fontSize="small" />,  
};
const inflight = new Map();

function rowsFromTable(table) {
  const rows = [];
  for (let i = 0; i < table.numRows; i++) {
    const row = {};
    for (const field of table.schema.fields) {
      const name = field.name;
      row[name] = table.getChild(name).get(i);
    }
    rows.push(row);
  }
  return rows;
}

export async function fetchMetrics({
  level,
  areaCodes = [],
  groupKeys = [],
  filters = {},
  theme = "pol",
  schema,
  normalise_over_options = false,
}) {
  const key = `${level}:${areaCodes.join(",")}:${groupKeys.join(",")}:${JSON.stringify(filters)}:${theme}:${normalise_over_options}`;

  if (inflight.has(key)) return inflight.get(key);

  const p = (async () => {
    const body = {
      theme,
      groupKeys,
      filters,
      areaCodes: areaCodes?.length ? areaCodes : [],
      ...schemaRequestFields(schema),
      normalise_over_options,
    };

    console.log("➡️ fetchMetrics payload", { level, ...body });

    const res = await fetch(`/api/${level}_data_arrow/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) throw new Error(`metrics ${res.status}`);

    const buf = await res.arrayBuffer();
    const rows = rowsFromTable(tableFromIPC(buf));

    console.log("⬅️ fetchMetrics rows", {
      level,
      constituencyCode: body.constituencyCode,
      numRows: rows?.length,
      exampleRow: rows?.[0],
    });

    return rows;
  })();

  inflight.set(key, p);
  try {
    return await p;
  } finally {
    inflight.delete(key);
  }
}



/* ─── Wave-series helpers ────────────────────────────────────────────────── */

/** Numeric coercion that keeps "absent" distinct from 0. */
const _num = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const _waveOf = (row) => Number(row?.Wave ?? row?.wave);

/** True when the row carries a value for at least one of the group's keys. */
const _hasAnyValue = (row, keys) => (keys || []).some((k) => _num(row?.[k]) != null);

/**
 * Newest-first list of `{ wave, row }` for a group.
 *
 * Rows with no value for any of the group's keys are dropped, so a wave that
 * exists in the backend but didn't ask this question can't blank out the
 * result — the previous wave is used instead.
 */
function _orderedWaveRows(rows, allowedWaves, groupKeys) {
  if (!Array.isArray(rows) || !rows.length) return [];
  const first = rows[0] || {};
  // Single-wave schemas (and any row set with no wave column) are one
  // snapshot with an unknown wave number.
  if (!("Wave" in first) && !("wave" in first)) {
    return _hasAnyValue(first, groupKeys) ? [{ wave: null, row: first }] : [];
  }
  return rows
    .filter((r) => !allowedWaves || allowedWaves.has(_waveOf(r)))
    .filter((r) => _hasAnyValue(r, groupKeys))
    .map((r) => ({ wave: _waveOf(r), row: r }))
    .sort((a, b) => b.wave - a.wave);
}

export async function fetchNational({
  groupKeys = [],
  filters = {},
  theme = "pol",
  schema,
  normalise_over_options = false,
}) {
  try {
    const natRows = await fetchMetrics({
      level: "national",
      areaCodes: [],
      groupKeys,
      filters,
      theme,
      schema,
      normalise_over_options,
    });
    if (natRows?.length) return natRows;
    console.warn("[fetchNational] empty national rows; falling back to constituency average", { theme });
  } catch (e) {
    console.warn("[fetchNational] error; falling back to constituency average", { theme, error: e });
  }

  const allRows = await fetchMetrics({
    level: "constituency",
    areaCodes: [],
    groupKeys,
    filters,
    theme,
    schema,
    normalise_over_options,
  });

  if (!allRows?.length) return [];

  // Aggregate the constituency rows up to GB ourselves.
  //
  // Three things matter here. (1) Keep the waves apart: the constituency request
  // returns one row per (area, wave), so pooling them would blend waves and
  // report a national figure that matches no fieldwork date — and would make
  // any wave-on-wave comparison meaningless. (2) Weight: a plain mean of 632
  // constituencies treats a small seat and a large one alike. (3) Weight each
  // key by the column the backend divided by — n_voters for vote-share
  // questions, n_raked otherwise — so this GB figure shares a basis with the
  // map and sidebar. See utils/keyWeights.js.
  const byWave = new Map();
  for (const r of allRows) {
    const wave = r?.Wave ?? r?.wave ?? null;
    const bucketKey = wave == null ? "__all__" : String(wave);
    let bucket = byWave.get(bucketKey);
    if (!bucket) {
      bucket = { wave, weight: 0, num: {}, den: {} };
      byWave.set(bucketKey, bucket);
    }
    // Reported N stays the raked sample size regardless of which base the
    // percentages used, so a row's n_raked is what accumulates here.
    bucket.weight += weightFromRowByCol(r, DEFAULT_WEIGHT_COL) ?? 1;
    for (const key of groupKeys) {
      // _num, not Number(): a question not asked in this wave comes back as
      // NULL, and Number(null) is 0 — which would drag the GB figure down and
      // invent a wave-on-wave change out of nothing.
      const v = _num(r?.[key]);
      if (v == null) continue;
      const weight = weightForRow(r, key) ?? 1;
      bucket.num[key] = (bucket.num[key] || 0) + v * weight;
      bucket.den[key] = (bucket.den[key] || 0) + weight;
    }
  }

  return [...byWave.values()]
    .sort((a, b) => Number(b.wave ?? 0) - Number(a.wave ?? 0))
    .map(({ wave, weight, num, den }) => {
      const row = { n_raked: weight };
      if (wave != null) row.Wave = wave;
      for (const key of groupKeys) {
        row[key] = den[key] ? num[key] / den[key] : null;
      }
      return row;
    });
}

/**
 * Like loadGroupData, but keeps the previous wave alongside the latest one so
 * callers can show wave-on-wave change.
 *
 * Returns `{ rows, wave, prevWave, waves }`:
 *   rows      — one per group key, with `constituency`/`national` at the latest
 *               wave and `prevConstituency`/`prevNational` + `delta`/
 *               `deltaNational` for the wave before it. Any of these is null
 *               when the wave doesn't carry that key (e.g. a question added or
 *               dropped between waves) — null means "not asked", never 0.
 *   wave      — the wave the headline figures come from (null for single-wave
 *               schemas).
 *   prevWave  — the comparison wave, or null when there's only one.
 *   waves     — every wave with data, newest first.
 */
export async function loadGroupWaves({ groupId, areaCode, filters = {}, theme }) {
  const { groupsById } = getPolConfig();
  const group = groupsById[groupId];
  if (!group) throw new Error(`Group '${groupId}' not found`);

  const groupKeys = group.keys || [];
  const labels = group.labels || {};
  const colors = group.colors || {};
  // Default must match the backend's ("tracking" when schema is absent) — the
  // schema_type we post is derived from this and now overrides the server's
  // own derivation. An object here (the old `|| {}`) would classify as single.
  const schema = group.schema || "tracking";
  const normalise_over_options = group.normalise_over_options || false;
  const inferredTheme =
    theme ||
    group.theme ||
    (groupKeys.some((k) => String(k).startsWith("ind:")) ? "ind" : "pol");

  const [rowsConst, rowsNat] = await Promise.all([
    fetchMetrics({ level: "constituency", areaCodes: [String(areaCode)], groupKeys, filters, schema, theme: inferredTheme, normalise_over_options }),
    fetchNational({ groupKeys, filters, schema, theme: inferredTheme, normalise_over_options }),
  ]);

  const matchesArea = (r) =>
    String(r?.area_cd ?? "") === String(areaCode) ||
    String(r?.area_id ?? "") === String(areaCode) ||
    String(r?.PCON24CD ?? "") === String(areaCode) ||
    String(r?.WD24CD ?? "") === String(areaCode);

  const allowedWaves = Array.isArray(group?.waves) && group.waves.length
    ? new Set(group.waves.map(Number))
    : null;

  const constRows = Array.isArray(rowsConst) ? rowsConst.filter(matchesArea) : [];
  const constSeries = _orderedWaveRows(
    constRows.length ? constRows : rowsConst, allowedWaves, groupKeys);
  const natSeries = _orderedWaveRows(rowsNat, allowedWaves, groupKeys);

  // Wave selection normally follows the constituency series. In national scope
  // there is no constituency row (the "GB" area query is empty), so drive it
  // from the national series instead — otherwise the national report loses its
  // headline wave, previous wave, and every wave-on-wave delta.
  const waveSeries = constSeries.length ? constSeries : natSeries;
  const latest = waveSeries[0] || null;
  const prev   = waveSeries[1] || null;

  // Read national at the same wave as the constituency figure so the two are
  // comparable. A wave-less national row (single-wave schema) pairs with the
  // headline figure only — reusing it as the previous wave would report a
  // national change of exactly zero, which is not the same as "unknown".
  const natIsWaveless = natSeries.length > 0 && natSeries[0].wave == null;
  const natAt = (wave) => natSeries.find((x) => x.wave === wave)?.row || null;
  // Pair national with the headline wave. `latest`/`prev` come from `waveSeries`,
  // which is the national series in national scope — so this aligns correctly in
  // both scopes (and gives national its previous-wave row for deltas).
  const rowNat     = natIsWaveless ? natSeries[0].row : natAt(latest?.wave ?? null);
  const rowNatPrev = (natIsWaveless || !prev) ? null : natAt(prev.wave);

  const data = (groupKeys || []).map(k => {
    const cNow  = _num(latest?.row?.[k]);
    const cPrev = _num(prev?.row?.[k]);
    const nNow  = _num(rowNat?.[k]);
    const nPrev = _num(rowNatPrev?.[k]);
    return {
      key: k,
      label: (labels && labels[k]) || k,
      constituency: cNow,
      national: nNow,
      prevConstituency: cPrev,
      prevNational: nPrev,
      delta:         (cNow != null && cPrev != null) ? cNow - cPrev : null,
      deltaNational: (nNow != null && nPrev != null) ? nNow - nPrev : null,
      color: (colors && colors[k]) || "#888",
    };
  });

  console.log("📊 Loaded group:", groupId, {
    areaCode,
    matchFound: Boolean(latest),
    wave: latest?.wave ?? null,
    prevWave: prev?.wave ?? null,
    rowArea: latest?.row?.area_cd,
  });

  return {
    rows: data,
    wave: latest?.wave ?? null,
    prevWave: prev?.wave ?? null,
    waves: waveSeries.map((x) => x.wave),
  };
}

/**
 * Latest-wave values for a group: `[{ key, label, constituency, national, color }]`.
 *
 * Thin wrapper over loadGroupWaves — the rows also carry the previous wave's
 * values (`prevConstituency`, `delta`, …) for callers that want them.
 */
export async function loadGroupData(args) {
  const { rows } = await loadGroupWaves(args);
  return rows;
}


export function BarCompareConstNat({
  data = [],
  title,
  constName,
  nationalLabel = "National",
  height,
  firstRowBadge,   // optional ReactNode rendered inline after the first row's label
  scaleMax,        // optional floor for the x-axis max (e.g. 75 prevents bars filling completely)
  barThickness = 7,   // constituency bar height (px)
  natThickness = 4,   // national bar height (px)
  maxRows = 10,       // max response rows to show
  hideNational = false, // hide the national comparison (value + bar) — for national reports
  // The comparison bar defaults to the series colour at low opacity, which
  // reads as "the same thing, benchmarked". When it holds a different
  // population rather than the same one nationally — the McCain report puts a
  // party's voters there — these let it carry that party's own colour at full
  // strength. Defaults reproduce the original comparison bar exactly.
  natColor,
  natOpacity = 0.28,
  // Colour the bracketed comparison value by its gap to the main value: green
  // when it sits above, red when below. Only meaningful when the comparison is
  // a different population rather than the same one at a wider geography, so
  // it is opt-in and off by default.
  natDeltaHighlight = false,
}) {
  const containerRef = useRef(null);
  const [measuredH, setMeasuredH] = useState(height ?? 300);

  useEffect(() => {
    if (height != null) { setMeasuredH(height); return; }
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      if (h > 0) setMeasuredH(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [height]);

  const sorted = useMemo(() => {
    return [...data]
      .filter((d) => Number.isFinite(d.constituency) && d.constituency !== 0)
      .sort((a, b) => (b.constituency ?? -Infinity) - (a.constituency ?? -Infinity))
      .slice(0, maxRows);
  }, [data, maxRows]);

  const maxVal = useMemo(
    () => Math.max(scaleMax ?? 0, ...sorted.flatMap((d) => [d.constituency ?? 0, d.national ?? 0]), 1),
    [sorted, scaleMax]
  );

  // Each row: label line + two bars + gap. Fully div-based — no recharts clipping.
  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: height != null ? height : "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        overflow: "hidden",
      }}
    >
      {title && (
        <div style={{ fontWeight: 700, marginBottom: 6, fontSize: "0.85rem", color: "#0f172a" }}>{title}</div>
      )}
      {sorted.map((d, i) => {
        const constPct  = Number.isFinite(d.constituency) ? d.constituency : 0;
        const natPct    = Number.isFinite(d.national)     ? d.national     : 0;
        const constW    = `${Math.max(0, (constPct / maxVal) * 100).toFixed(2)}%`;
        const natW      = `${Math.max(0, (natPct  / maxVal) * 100).toFixed(2)}%`;
        const color     = d.color || "#0abfbc";

        // Gaps under 0.1pp are noise at one decimal place, so they stay neutral
        // rather than showing an arrow that the printed numbers contradict.
        const natDelta  = natPct - constPct;
        const natMoved  = natDeltaHighlight && Math.abs(natDelta) >= 0.1;
        const natTone   = !natMoved ? "#94a3b8" : natDelta > 0 ? "#15803d" : "#b91c1c";

        return (
          <div
            key={i}
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              paddingBottom: i < sorted.length - 1 ? 3 : 0,
              borderBottom: i < sorted.length - 1 ? "0.5px solid #f1f5f9" : "none",
            }}
          >
            {/* Label + value on one line */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 3,
              gap: 4,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, flex: 1, minWidth: 0 }}>
                <span style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#334155",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                  {d.label}
                </span>
                {i === 0 && firstRowBadge && firstRowBadge}
              </div>
              <span style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: "#0f172a",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}>
                {constPct.toFixed(1)}%
                {!hideNational && (
                  <span style={{
                    fontSize: 9,
                    // Direction is carried by colour and weight only. An arrow
                    // used to sit here too, but next to a bracketed figure it
                    // read as ambiguous rather than as a direction cue.
                    fontWeight: natMoved ? 700 : 400,
                    color: natTone,
                    marginLeft: 4,
                  }}>
                    ({natPct.toFixed(1)}%)
                  </span>
                )}
              </span>
            </div>

            {/* Constituency bar */}
            <div style={{
              height: barThickness,
              borderRadius: barThickness / 2,
              backgroundColor: "#f1f5f9",
              marginBottom: 2,
              overflow: "hidden",
            }}>
              <div style={{
                height: "100%",
                width: constW,
                backgroundColor: color,
                borderRadius: barThickness / 2,
                opacity: 0.85,
                transition: "width 0.4s ease",
              }} />
            </div>

            {/* National bar (hidden in national reports) */}
            {!hideNational && (
              <div style={{
                height: natThickness,
                borderRadius: natThickness / 2,
                backgroundColor: "#f1f5f9",
                overflow: "hidden",
              }}>
                <div style={{
                  height: "100%",
                  width: natW,
                  backgroundColor: natColor || color,
                  borderRadius: natThickness / 2,
                  opacity: natOpacity,
                  transition: "width 0.4s ease",
                }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export async function fetchConstituencyBoundsFromGeo(areaCode) {
  const url = geoAssetUrl("/ukmaps/2024_constituency.json");
  const fc = await fetch(url).then(r => r.json());
  const feat = (fc.features || []).find(f => String(f.properties?.PCON24CD) === String(areaCode));
  if (!feat?.geometry) return null;

  const coords = feat.geometry.type === "Polygon"
    ? feat.geometry.coordinates.flat(2)
    : feat.geometry.coordinates.flat(3);

  const lons = coords.filter((_, i) => i % 2 === 0);
  const lats = coords.filter((_, i) => i % 2 === 1);
  const minLng = Math.min(...lons), maxLng = Math.max(...lons);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  return [[minLng, minLat], [maxLng, maxLat]];
}
export async function fetchWardGroup(areaCode, groupId, filters = {}) {
  const { groupsById } = getPolConfig();
  const group = groupsById[groupId];
  if (!group) throw new Error(`Group '${groupId}' not found in getPolConfig()`);

  const groupKeys = group.keys;

  const allRows = await fetchMetrics({
    level: "ward",
    areaCodes: [String(areaCode)],
    groupKeys,
    filters,
    theme: "pol",
    schema: group.schema,
    normalise_over_options: group.normalise_over_options || false,
  });

  // If the group declares which waves it belongs to, keep only those rows
  // so a newer wave added to the backend doesn't overwrite with empty data.
  const allowedWaves = Array.isArray(group.waves) && group.waves.length
    ? new Set(group.waves.map(Number))
    : null;

  const rows = allowedWaves && Array.isArray(allRows)
    ? allRows.filter((r) => r.Wave == null || allowedWaves.has(Number(r.Wave)))
    : allRows;

  return { rows, colors: group.colors, labels: group.labels, keys: groupKeys, schema: group.schema };
}

// All-GB constituency rows for a group (latest valid wave per constituency).
// Used to colour the national constituency choropleth.
export async function fetchConstituencyGroup(groupId, filters = {}) {
  const { groupsById } = getPolConfig();
  const group = groupsById[groupId];
  if (!group) throw new Error(`Group '${groupId}' not found in getPolConfig()`);

  const groupKeys = group.keys || [];
  const theme = group.theme || (String(groupKeys[0] || "").startsWith("ind:") ? "ind" : "pol");

  const allRows = await fetchMetrics({
    level: "constituency",
    areaCodes: [],
    groupKeys,
    filters,
    theme,
    schema: group.schema,
    normalise_over_options: group.normalise_over_options || false,
  });

  const allowedWaves = Array.isArray(group.waves) && group.waves.length
    ? new Set(group.waves.map(Number)) : null;

  // Keep the latest valid wave per constituency.
  const byArea = new Map();
  (allRows || []).forEach((r) => {
    if (allowedWaves && r.Wave != null && !allowedWaves.has(Number(r.Wave))) return;
    const code = String(r.area_cd || r.PCON24CD || r.area_id || "");
    if (!code) return;
    const w = Number(r.Wave) || 0;
    const prev = byArea.get(code);
    if (!prev || w >= prev._w) byArea.set(code, { ...r, _w: w });
  });

  return { rows: Array.from(byArea.values()), colors: group.colors, labels: group.labels, keys: groupKeys };
}

// National constituency choropleth (all of GB), styled to match the ward maps.
// Colours each constituency by its leading response (top key), or by its top
// option when `options` (grid aspects) is supplied. `colors` overrides group.colors.
export function ConstituencyGroupStaticMap({ groupId, height, colors: colorsOverride, options }) {
  const containerRef = useRef(null);
  const [dims, setDims] = useState(null);
  const [svgData, setSvgData] = useState({ paths: [] });
  const [mapUrl, setMapUrl] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width: w, height: h } = entry.contentRect;
      if (w > 0 && h > 0) setDims({ w: Math.round(w), h: Math.round(h) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!dims || !groupId) return;
    let cancelled = false;
    (async () => {
      const url = geoAssetUrl("/ukmaps/2024_constituency.json");
      const fcAll = await fetch(url).then((r) => r.json());
      // GB only — exclude Northern Ireland (PCON24CD starts with "N"), matching
      // the main map. NI has no survey data here anyway.
      const feats = (fcAll.features || []).filter(
        (f) => !String(f.properties?.PCON24CD || "").startsWith("N")
      );
      if (!feats.length) { if (!cancelled) setSvgData({ paths: [] }); return; }

      // Generous padding (and extra room for the caption bar) so the map zooms
      // out enough that the extremities (e.g. Cornwall, N. Scotland) aren't clipped.
      const PAD = 14;
      const CAPTION = 16;
      const proj = geoMercator().fitExtent(
        [[PAD, PAD], [dims.w - PAD, dims.h - PAD - CAPTION]],
        { type: "FeatureCollection", features: feats }
      );
      const path = geoPath(proj);
      const [centerLng, centerLat] = proj.invert([dims.w / 2, dims.h / 2]);
      const zoom = Math.log2(proj.scale() * 2 * Math.PI / 512);
      if (!cancelled) setMapUrl(generateWardMapImage(centerLng, centerLat, zoom, dims.w, dims.h));

      const { rows, colors: fetchedColors, keys } = await fetchConstituencyGroup(groupId);
      const colors = (colorsOverride && Object.keys(colorsOverride).length) ? colorsOverride : fetchedColors;
      const byArea = new Map();
      rows.forEach((r) => byArea.set(String(r.area_cd || r.PCON24CD || r.area_id), r));

      const useOptions = Array.isArray(options) && options.length > 0;
      const paths = feats.map((f) => {
        const code = String(f.properties?.PCON24CD);
        const row = byArea.get(code) || {};
        let fill = "#e5e7eb";
        if (useOptions) {
          let topOpt = null, topVal = -Infinity;
          for (const o of options) {
            const v = (o.sumKeys || [o.key]).reduce((acc, k) => {
              const x = Number(row[k]);
              return Number.isFinite(x) ? acc + x : acc;
            }, 0);
            if (v > topVal) { topVal = v; topOpt = o; }
          }
          if (topOpt && topVal > -Infinity) fill = topOpt.color || "#e5e7eb";
        } else {
          let topKey = null, topVal = -Infinity;
          for (const k of keys) {
            const v = Number(row[k]);
            if (Number.isFinite(v) && v > topVal) { topVal = v; topKey = k; }
          }
          if (topKey) fill = colors?.[topKey] || "#e5e7eb";
        }
        return { d: path(f), fill };
      });

      if (!cancelled) setSvgData({ paths });
    })();
    return () => { cancelled = true; };
  }, [groupId, dims, options]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative", width: "100%",
        height: height != null ? `${height}px` : "100%",
        overflow: "hidden", border: "1px solid #ccc", borderRadius: 4,
      }}
    >
      {dims && mapUrl && (
        <img
          src={mapUrl}
          alt="Base map"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          style={{ position: "absolute", top: 0, left: 0, width: `${dims.w}px`, height: `${dims.h}px` }}
        />
      )}
      {dims && (
        <svg viewBox={`0 0 ${dims.w} ${dims.h}`} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
          {svgData.paths.map((p, i) => (
            <path key={i} d={p.d} fill={p.fill} fillOpacity={0.75} stroke="#fff" strokeWidth={0.3} />
          ))}
        </svg>
      )}
    </div>
  );
}

export const fetchWardVI = (areaCode, filters={}) => fetchWardGroup(areaCode, "vote_intention", filters);

const MAPBOX_TOKEN = "pk.eyJ1IjoibXppZW1pYW4iLCJhIjoiY2xhOXdpc2h5MDI1MzNxbG1wdzcxOWNlZSJ9.Km0c9-jUUlcBs6ByXYbYZA";

/**
 * Generate a static Mapbox image centered on the constituency.
 * Optionally use ward-level VI overlay in the future (for now base map).
 * @param {string} constituencyCode - e.g. "E14000639"
 * @param {[[number, number], [number, number]]} bounds - [[minLng,minLat],[maxLng,maxLat]]
 * @param {number} [width=600] - Image width in pixels
 * @param {number} [height=400] - Image height in pixels
 * @returns {string} Static map image URL (PNG)
 */
export function generateWardMapImage(centerLng, centerLat, zoom, width = 600, height = 400) {
  const token = MAPBOX_TOKEN;

  if (!token) {
    console.error("❌ Missing Mapbox token REACT_APP_MAPBOX_TOKEN");
  }

  // if (!bounds || !Array.isArray(bounds) || bounds.length !== 2) {
  //   console.warn("⚠️ generateWardMapImage: invalid bounds, using default");
  //   return `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/-2.5,54.5,5/${width}x${height}?access_token=${token}`;
  // }

  //const [[minLng, minLat], [maxLng, maxLat]] = bounds;

  // bbox endpoint guarantees image covers exactly this extent — no zoom guessing
  return `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/` +
         `${centerLng},${centerLat},${zoom}/` +
         `${width}x${height}?access_token=${token}`;
}



export function WardGroupStaticMapWithBase({
  groupId,
  areaCode,
  bounds,
  height,
  fillOpacity = 0.72,
  colors: colorsOverride,   // optional { key: color } — overrides group.colors (e.g. derived likert colours)
  options,                  // optional [{ key, sumKeys, color }] — colour each ward by its top option (e.g. a likert-grid aspect) instead of top single key
}) {
  const containerRef = useRef(null);
  const [dims, setDims] = useState(null);
  const [svgData, setSvgData] = useState({ paths: [] });
  const [mapUrl, setMapUrl] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width: w, height: h } = entry.contentRect;
      if (w > 0 && h > 0) setDims({ w: Math.round(w), h: Math.round(h) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!dims) return;
    let cancelled = false;
    (async () => {
      const url = geoAssetUrl("/ukmaps/2024_ward_withPCON.json");
      const fcAll = await fetch(url).then((r) => r.json());
      const wards = (fcAll.features || []).filter(
        (f) => String(f.properties?.PCON24CD) === String(areaCode)
      );
      if (!wards.length) {
        if (!cancelled) setSvgData({ paths: [] });
        return;
      }

 const PADDING = 20;

const proj = geoMercator().fitExtent(
  [[PADDING, PADDING], [dims.w - PADDING, dims.h - PADDING]],
  { type: "FeatureCollection", features: wards }
);
const path = geoPath(proj);

const [centerLng, centerLat] = proj.invert([dims.w / 2, dims.h / 2]);
const zoom = Math.log2(proj.scale() * 2 * Math.PI / 512);

      if (!cancelled) setMapUrl(
        generateWardMapImage(centerLng, centerLat, zoom, dims.w, dims.h)
      );

      const { rows, colors: fetchedColors, keys } = await fetchWardGroup(areaCode, groupId);
      const colors = (colorsOverride && Object.keys(colorsOverride).length) ? colorsOverride : fetchedColors;
      // Keep the LATEST wave per ward — rows can span multiple waves and taking
      // an arbitrary one leads to the map disagreeing with wave-latest tables.
      const byWard = new Map();
      rows.forEach((r) => {
        const wid = String(r.WD24CD || r.area_cd || r.area_id);
        const w = Number(r.Wave) || 0;
        const prev = byWard.get(wid);
        if (!prev || w >= prev._w) byWard.set(wid, { ...r, _w: w });
      });

      const useOptions = Array.isArray(options) && options.length > 0;
      const paths = wards.map((f) => {
        const wcode = String(f.properties?.WD24CD);
        const row = byWard.get(wcode) || {};
        let fill = "#cccccc";
        if (useOptions) {
          // Colour by the top OPTION (sum of its sumKeys) — e.g. the leading
          // aspect of a likert grid — using that option's colour.
          let topOpt = null, topVal = -Infinity;
          for (const o of options) {
            const v = (o.sumKeys || [o.key]).reduce((acc, k) => {
              const x = Number(row[k]);
              return Number.isFinite(x) ? acc + x : acc;
            }, 0);
            if (v > topVal) { topVal = v; topOpt = o; }
          }
          if (topOpt && topVal > -Infinity) fill = topOpt.color || "#cccccc";
        } else {
          let topKey = null, topVal = -Infinity;
          for (const k of keys) {
            const v = Number(row[k]);
            if (Number.isFinite(v) && v > topVal) { topVal = v; topKey = k; }
          }
          if (topKey) fill = colors?.[topKey] || "#cccccc";
        }
        return {
          d: path(f),
          fill,
          name: f.properties?.WD24NM || wcode,
        };
      });

      if (!cancelled) setSvgData({ paths });
    })();
    return () => { cancelled = true; };
  }, [groupId, areaCode, dims, options]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: height != null ? `${height}px` : "100%",
        overflow: "hidden",
        border: "1px solid #ccc",
        borderRadius: 4,
      }}
    >
      {dims && mapUrl && (
        <img
          src={mapUrl}
          alt="Base map"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${dims.w}px`,
            height: `${dims.h}px`,
          }}
        />
      )}

      {dims && (
        <svg
          viewBox={`0 0 ${dims.w} ${dims.h}`}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {svgData.paths.map((p, i) => (
            <path
              key={i}
              d={p.d}
              fill={p.fill}
              fillOpacity={fillOpacity}
              stroke="#fff"
              strokeWidth={0.6}
            />
          ))}
        </svg>
      )}
    </div>
  );
}

export function WardVIStaticMapWithBase({
  areaCode,
  bounds,
  width = 360,
  height = 260,
}) {
  const [svgData, setSvgData] = useState({ paths: [] });
  const [mapUrl, setMapUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!bounds) {
        const b = await fetchConstituencyBoundsFromGeo(areaCode);
        setMapUrl(generateWardMapImage(b, width, height));
      } else {
        setMapUrl(generateWardMapImage(bounds, width, height));
      }

      const url = geoAssetUrl("/ukmaps/2024_ward_withPCON.json");
      const fcAll = await fetch(url).then((r) => r.json());
      const wards = (fcAll.features || []).filter(
        (f) => String(f.properties?.PCON24CD) === String(areaCode)
      );
      if (!wards.length) return;

      const { rows, colors, keys } = await fetchWardVI(areaCode);
      const byWard = new Map();
      rows.forEach((r) => byWard.set(String(r.WD24CD || r.area_cd), r));

      const proj = geoMercator().fitExtent([[8, 8], [width - 8, height - 8]], {
        type: "FeatureCollection",
        features: wards,
      });
      const path = geoPath(proj);

      const paths = wards.map((f) => {
        const wcode = String(f.properties?.WD24CD);
        const row = byWard.get(wcode) || {};
        let topKey = null,
          topVal = -Infinity;
        for (const k of keys) {
          const v = Number(row[k]);
          if (Number.isFinite(v) && v > topVal) {
            topVal = v;
            topKey = k;
          }
        }
        return {
          d: path(f),
          fill: (topKey && colors[topKey]) || "#cccccc",
          name: f.properties?.WD24NM || wcode,
        };
      });

      if (!cancelled) setSvgData({ paths });
    })();
    return () => {
      cancelled = true;
    };
  }, [areaCode, bounds, width, height]);

  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        overflow: "hidden",
        border: "1px solid #ccc",
      }}
    >
      {mapUrl && (
        <img
          src={mapUrl}
          alt="Base map"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}

      <svg
        width={width}
        height={height}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      >
        {svgData.paths.map((p, i) => (
          <path
            key={i}
            d={p.d}
            fill={p.fill}
            fillOpacity={0.7}
            stroke="#fff"
            strokeWidth={0.6}
          />
        ))}
      </svg>
    </div>
  );
}

export function WardGroupLegend({ groupId, swatchSize = 10 }) {
  const { groupsById } = getPolConfig();
  const g = groupsById[groupId];
  if (!g) return null;
  const { labels = {}, colors = {}, keys = [] } = g;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
      {keys.map((k) => (
        <div key={k} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: swatchSize, height: swatchSize, background: colors[k], display: "inline-block", border: "1px solid #999" }} />
          <span style={{ fontSize: 12 }}>{labels[k] || k}</span>
        </div>
      ))}
    </div>
  );
}
export function BestPMGrid({
  data = [],
  title = "Best Prime Minister",
  height = "100%",
  showTitle = true,
  hideNational = false,
}) {
  // Drop options with no data at the displayed wave (e.g. a candidate who left
  // the field in a later wave) so they don't render as empty rows.
  const sorted = [...(data || [])]
    .filter((d) => d.constituency != null)
    .sort((a, b) => (b.constituency ?? -Infinity) - (a.constituency ?? -Infinity));
  const winner = sorted[0];
  const others = sorted.slice(1);

  return (
    <Box sx={{ height, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {showTitle && (
        <Typography sx={{ mb: 0.75, fontWeight: 700, fontSize: 11, color: "#0f172a", letterSpacing: "0.01em" }}>
          {title}
        </Typography>
      )}

      <Box sx={{ flexGrow: 1, display: "grid", gridTemplateColumns: "auto 1fr", gap: "14px", minHeight: 0, height: "100%", alignItems: "stretch" }}>

        {/* Winner — compact featured card, pinned to top */}
        {winner && (
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            border: "0.5px solid #e2e8f0",
            borderTop: `3px solid ${winner.color || "#0abfbc"}`,
            borderRadius: "4px",
            bgcolor: "#fff",
            px: "12px",
            py: "10px",
            minWidth: 120,
            gap: "5px",
          }}>
            {ICON_MAP[winner.label] ? (
              <Box sx={{ width: 52, height: 52, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `2px solid ${winner.color || "#e2e8f0"}` }}>
                {React.cloneElement(ICON_MAP[winner.label], { style: { width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%", display: "block" } })}
              </Box>
            ) : (
              <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: winner.color || "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <EmojiPeopleIcon sx={{ fontSize: 26, color: "#fff" }} />
              </Box>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <Typography sx={{ fontWeight: 800, fontSize: 11, color: "#0f172a", lineHeight: 1.2 }}>
                {winner.label}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 20, color: winner.color || "#0f172a", lineHeight: 1 }}>
                {winner.constituency?.toFixed(1)}%
              </Typography>
              {!hideNational && winner.national != null && (
                <Typography sx={{ fontSize: 9, color: "#64748b", lineHeight: 1 }}>
                  {winner.national.toFixed(1)}% nationally
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Others — compact rows */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", justifyContent: "space-evenly", height: "100%", minHeight: 0 }}>
          {others.map((d, i) => (
            <Box key={i} sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              px: "10px",
              py: "4px",
              borderRadius: "3px",
              border: "0.5px solid #e2e8f0",
              borderLeft: `3px solid ${d.color || "#94a3b8"}`,
              bgcolor: d.color ? `${d.color}10` : "#f8fafc",
              overflow: "hidden",
              minHeight: 0,
            }}>
              <Typography sx={{ flex: 1, fontSize: 10, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {d.label}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px", flexShrink: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>
                  {d.constituency?.toFixed(1)}%
                </Typography>
                {!hideNational && d.national != null && (
                  <Typography sx={{ fontSize: 9, color: "#64748b" }}>
                    ({d.national.toFixed(1)}%)
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}



export function TopThreePrioritiesMini({ data = [], height = "100%", hideNational = false }) {
  const top3 = [...(data || [])]
    .filter(
      (d) =>
        Number.isFinite(d?.constituency) &&
        Math.abs(d.constituency) > 1e-6
    )
    .sort(
      (a, b) => (b.constituency ?? -Infinity) - (a.constituency ?? -Infinity)
    )
    .slice(0, 3);

  return (
    <Box sx={{ height, width: "100%", display: "flex", flexDirection: "column", gap: "5px", minHeight: 0 }}>
      {top3.map((d, i) => (
        <Box key={i} sx={{
          flex: 1,
          maxHeight: 52,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "10px",
          py: "4px",
          borderRadius: "3px",
          border: "0.5px solid #e2e8f0",
          borderLeft: `3px solid ${d.color || "#94a3b8"}`,
          bgcolor: d.color ? `${d.color}10` : "#f8fafc",
          width: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}>

          {/* Icon */}
          <Box sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#fff",
            border: `0.5px solid ${d.color || "#e2e8f0"}`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: d.color || "#64748b",
            flexShrink: 0,
          }}>
            {ICON_MAP_PRIORITIES[d.label]
              ? React.cloneElement(ICON_MAP_PRIORITIES[d.label], { sx: { fontSize: 13 } })
              : <WorkIcon sx={{ fontSize: 13 }} />}
          </Box>

          {/* Label */}
          <Typography sx={{ flex: 1, fontSize: 10.5, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {d.label}
          </Typography>

          {/* Numbers */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px", flexShrink: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
              {Number.isFinite(d.constituency) ? d.constituency.toFixed(1) : "—"}%
            </Typography>
            {!hideNational && (
              <Typography sx={{ fontSize: 9.5, color: "#64748b" }}>
                ({Number.isFinite(d.national) ? d.national.toFixed(1) : "—"}%)
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function generateComparisonBlurb(sortedData = [], constName = "This area") {
  if (!sortedData.length) return null;

  const colorByLabel = Object.fromEntries(
    sortedData.map(d => [d.label, d.color])
  );

  const diffs = sortedData.map(d => ({
    label: d.label,
    diff: (d.constituency ?? 0) - (d.national ?? 0),
  }));

  const positive = diffs
    .filter(d => d.diff > 0.5)
    .sort((a, b) => b.diff - a.diff)
    .slice(0, 3);

  if (!positive.length) {
    return (
      <span>
        {constName} aligns closely with national priorities, with no major differences in emphasis.
      </span>
    );
  }

  const parts = positive.map((d, idx) => {
    const color = colorByLabel[d.label] || "#1e88e5";
    return (
      <strong key={idx} style={{ color, fontWeight: 700 }}>
        {d.label}
      </strong>
    );
  });

  let pretty;
  if (parts.length === 1) {
    pretty = parts[0];
  } else if (parts.length === 2) {
    pretty = <>{parts[0]} and {parts[1]}</>;
  } else {
    pretty = (
      <>
        {parts[0]}, {parts[1]}, and {parts[2]}
      </>
    );
  }

  return (
    <span>
      {constName} places greater emphasis on {pretty} compared to the national average.
    </span>
  );
}

export function getTopVI(viData = []) {
  if (!Array.isArray(viData) || viData.length === 0) return null;

  return [...viData]
    .filter(d => Number.isFinite(d.constituency))
    .sort((a, b) => (b.constituency ?? 0) - (a.constituency ?? 0))[0];
}


export const TWFY_KEY = "GUf56MGYfJRkBvVqckCTPk7H";


const TWFY_BASE = "https://www.theyworkforyou.com";

async function fetchJson(url, label = "request") {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.error(`❌ ${label} failed with status`, res.status);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.error(`❌ ${label} failed:`, err);
    return null;
  }
}


export async function getTwfyMpByConstituency(constName) {
  if (!constName) return null;

  const url =
    `${TWFY_BASE}/api/getMP?` +
    `constituency=${encodeURIComponent(constName)}` +
    `&output=json&always_return=1&key=${TWFY_KEY}`;

  const json = await fetchJson(url, "TWFY getMP");
  if (!json) return null;

  if (Array.isArray(json)) {
    const current =
      json.find((m) => m.left_house === "9999-12-31") || json[0] || null;
    return current;
  }

  return json;
}


export async function getMpIdFromConstituency(constName) {
  if (!constName) return null;

  const url =
    `https://members-api.parliament.uk/api/Location/Constituency/Search?` +
    `searchText=${encodeURIComponent(constName)}`;

  const json = await fetchJson(url, "Parliament constituency search");
  return (
    json?.items?.[0]?.value?.currentRepresentation?.member?.value?.id || null
  );
}


export async function getMpDetails(memberId) {
  if (!memberId) return null;

  const endpoints = {
    biography: `https://members-api.parliament.uk/api/Members/${memberId}`,
    contact: `https://members-api.parliament.uk/api/Members/${memberId}/Contact`,
    portraitUrl: `https://members-api.parliament.uk/api/Members/${memberId}/PortraitUrl`,
    latestElection: `https://members-api.parliament.uk/api/Members/${memberId}/LatestElectionResult`,
    career: `https://members-api.parliament.uk/api/Members/${memberId}/Biography`,
  };

  const entries = await Promise.all(
    Object.entries(endpoints).map(async ([key, url]) => {
      const j = await fetchJson(url, `Parliament ${key}`);
      return [key, j];
    })
  );

  return Object.fromEntries(entries);
}


// Year the MP was first elected to the Commons, in preference order:
// Biography houseMemberships (earliest run) > current membership start > TWFY.
export function getFirstElectedYear(mpData) {
  if (!mpData) return null;

  // One entry per continuous run in the Commons; >1 means a break in service,
  // so the earliest start is the genuine first election.
  const commonsRuns = (mpData.career?.value?.houseMemberships || [])
    .filter((m) => m?.house === 1 && m?.startDate)
    .map((m) => m.startDate)
    .sort();
  if (commonsRuns.length) return commonsRuns[0].slice(0, 4);

  // Start of the current unbroken membership. Survives re-elections, boundary
  // renames and seat changes, but a break in service would hide the earlier run.
  const current =
    mpData.biography?.value?.latestHouseMembership?.membershipStartDate;
  if (current) return current.slice(0, 4);

  // TWFY resets entered_house on re-election, defection and whip changes.
  const twfy = mpData.twfy?.entered_house;
  return twfy ? twfy.slice(0, 4) : null;
}


// Current committee seats and roles, newest first within each category.
//
// Sourced from Parliament's Biography endpoint, which dates every post, so
// `endDate === null` reliably means "still holds it". TWFY's office list is not
// usable for this: it stamps every entry with to_date 9999-12-31 ("current") and
// goes stale when a government changes — it still reported Kemi Badenoch as
// Minister for Women and Equalities long after she moved to the opposition
// benches. TWFY is kept only as a fallback for when the Parliament call fails.
export function getCurrentRoles(mpData) {
  const career = mpData?.career?.value;

  const typeOrder = {
    Government: 0,
    Opposition: 1,
    Parliamentary: 2,
    Committee: 3,
  };

  let roles;

  if (career) {
    // A committee shows its own name as the headline and "Member" beneath it;
    // a post shows the role title, so it leaves `dept` empty.
    const current = (key, type) =>
      (career[key] || [])
        .filter((p) => p && !p.endDate && p.house !== 2 && p.name)
        .map((p) => ({
          type,
          position: type === "Committee" ? "Member" : p.name,
          dept: type === "Committee" ? p.name : "",
          from: (p.startDate || "").slice(0, 4),
          to: "Present",
        }));

    roles = [
      ...current("governmentPosts", "Government"),
      ...current("oppositionPosts", "Opposition"),
      ...current("otherPosts", "Parliamentary"),
      ...current("committeeMemberships", "Committee"),
    ];
  } else {
    // Fallback only. Everything here claims to be current, so stale roles ride along.
    const classify = (o) => {
      const pos = o.position || "";
      if (/Shadow/i.test(pos)) return "Opposition";
      if (
        /Secretary of State|Minister|Chancellor|Whip|Attorney General|Solicitor General/i.test(
          pos
        )
      )
        return "Government";
      if (o.dept) return "Committee";
      return "Parliamentary";
    };

    roles = (Array.isArray(mpData?.twfy?.office) ? mpData.twfy.office : []).map(
      (o) => ({
        type: classify(o),
        position: o.position || "Member",
        dept: o.dept || "",
        from: (o.from_date || "").slice(0, 4),
        to: o.to_date === "9999-12-31" ? "Present" : (o.to_date || "").slice(0, 4),
      })
    );
  }

  return roles.sort(
    (a, b) =>
      (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99) ||
      (b.from || "").localeCompare(a.from || "")
  );
}


export async function loadFullMpData(constName) {
  if (!constName) return null;

  const [twfyMp, memberId] = await Promise.all([
    getTwfyMpByConstituency(constName),
    getMpIdFromConstituency(constName),
  ]);

  let core = null;
  if (memberId) {
    core = await getMpDetails(memberId);
  }

  return {
    ...(core || {}),
    twfy: twfyMp || null, 
  };
};

export function MPCard({ mpData }) {
  if (!mpData) {
    return (
      <Box
        sx={{
          borderRadius: 2,
          p: 1,
          mt: 1,
          height: "100%",
          overflow: "hidden",
          bgcolor: "#F5F5F7",
          border: "1px solid #E0E0E5",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading MP information…
        </Typography>
      </Box>
    );
  }

  const twfy = mpData.twfy || null;

  const fullName = twfy?.full_name || mpData.biography?.value?.nameDisplayAs;
  const constituency = twfy?.constituency;
  const partyFromTwfy = twfy?.party;
  const firstElectedYear = getFirstElectedYear(mpData);

  const twfyImage = twfy?.image
    ? `https://www.theyworkforyou.com${twfy.image}`
    : null;


  const bio = mpData.biography?.value;
  const portraitParl = mpData.portraitUrl?.value;
  function proxyIfNeeded(url) {
    if (!url) return null;
    try {
      const host = new URL(url).hostname;
      if (
        host === "www.theyworkforyou.com" ||
        host === "members.parliament.uk"
      ) {
        return `/api/img-proxy/?url=${encodeURIComponent(url)}`;
      }
    } catch (_) {}
    return url;
  }
  
  const portrait = proxyIfNeeded(twfyImage || portraitParl);
  
  const latest = mpData.latestElection?.value;

  const contact = mpData.contact?.value || [];
  const primaryContact = contact[0] || {};
  const email = primaryContact.email || null;
  const phone = primaryContact.phone || null;

  const party = partyFromTwfy || bio?.party || "—";


  const normalisedParty = (party || "").toLowerCase();

  let partyColor = "#666666";
  if (normalisedParty.includes("labour")) partyColor = "#D50000";
  else if (normalisedParty.includes("conservative")) partyColor = "#0057B8";
  else if (
    normalisedParty.includes("liberal") ||
    normalisedParty.includes("lib dem")
  )
    partyColor = "#FDBB30";
  else if (
    normalisedParty.includes("scottish national") ||
    normalisedParty.includes("snp") ||
    normalisedParty === "snp"
  )
    partyColor = "#FEE500";
  else if (normalisedParty.includes("reform")) partyColor = "#00B2FF";
  else if (normalisedParty.includes("green")) partyColor = "#00843D";
  else if (normalisedParty.includes("plaid")) partyColor = "#3F8428";
  else if (normalisedParty.includes("speaker")) partyColor = "#6C757D";
  else if (normalisedParty.includes("independent")) partyColor = "#444444";

  const partyChipBg = `${partyColor}14`;
  const roleItems = getCurrentRoles(mpData);

  const hasRoles = roleItems.length > 0;

  return (
    <Box sx={{ overflow: "hidden", position: "relative" }}>
      <Box sx={{
        border: "0.5px solid #e2e8f0",
        borderRadius: "5px",
        bgcolor: "#fff",
        px: "10px",
        py: "8px",
        overflow: "hidden",
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>

          {/* Portrait */}
          <Box sx={{ flexShrink: 0 }}>
            {portrait ? (
              <Box
                component="img"
                src={portrait}
                alt={fullName || "MP portrait"}
                sx={{
                  width: 76,
                  height: 96,
                  objectFit: "cover",
                  borderRadius: "4px",
                  border: `1px solid ${partyColor}44`,
                  display: "block",
                }}
              />
            ) : (
              <Box sx={{
                width: 76,
                height: 96,
                borderRadius: "4px",
                border: "0.5px dashed #cbd5e1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                color: "#94a3b8",
              }}>
                No photo
              </Box>
            )}
          </Box>

          {/* Name + meta */}
          <Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#0f172a", lineHeight: 1.2, mb: "3px" }} noWrap>
              {fullName || "Member of Parliament"}
            </Typography>

            {constituency && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: "1px" }}>
                <LocationOnRoundedIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                <Typography sx={{ fontSize: 10, color: "#475569" }} noWrap>
                  MP for {constituency}
                </Typography>
              </Stack>
            )}

            {firstElectedYear && (
              <Typography sx={{ fontSize: 10, color: "#475569", mb: "3px" }} noWrap>
                First elected <b>{firstElectedYear}</b>
              </Typography>
            )}

            {email && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <EmailRoundedIcon sx={{ fontSize: 12, color: partyColor }} />
                <Typography
                  component="a"
                  href={`mailto:${email}`}
                  sx={{ fontSize: 9.5, color: "#0f172a", textDecoration: "none", "&:hover": { textDecoration: "underline" }, maxWidth: 200 }}
                  noWrap
                >
                  {email}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Committees & Roles */}
          <Stack sx={{ width: "52%", minWidth: 0, overflow: "hidden", borderLeft: "0.5px solid #e2e8f0", pl: "12px" }}>
            <Typography sx={{ fontSize: 8.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0abfbc", mb: "3px" }}>
              Committees &amp; Roles
            </Typography>
            {roleItems.length === 0 ? (
              <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                Not currently serving on any committees
              </Typography>
            ) : (
              <Box sx={{
                height: "calc(100% - 18px)",
                overflow: "hidden",
                columnCount: roleItems.length > 4 ? 2 : 1,
                columnGap: "12px",
              }}>
                {roleItems.slice(0, 6).map((r, idx) => {
                  const primary = r.dept || r.position;
                  const secondaryType = r.dept ? r.position : r.type;
                  const secondaryDates = r.from || r.to
                    ? ` (${r.from || ""}${r.from && r.to ? "–" : ""}${r.to || ""})`
                    : "";

                  const n = roleItems.length;
                  const primarySize = n >= 5 ? 9 : n >= 3 ? 10 : 11;
                  const secondarySize = n >= 5 ? 7.5 : n >= 3 ? 8 : 8.5;

                  return (
                    <Box key={idx} sx={{
                      breakInside: "avoid",
                      mb: n > 4 ? "2px" : "5px",
                      pl: "10px",
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "0.55em",
                        width: 3,
                        height: 3,
                        borderRadius: "50%",
                        bgcolor: "#0abfbc",
                        opacity: 0.7,
                      },
                    }}>
                      <Typography sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: `${primarySize}px`, color: "#0f172a", overflowWrap: "anywhere" }}>
                        {primary}
                      </Typography>
                      <Typography sx={{ fontSize: `${secondarySize}px`, color: "#64748b", lineHeight: 1.15, overflowWrap: "anywhere" }}>
                        {secondaryType}{secondaryDates}
                      </Typography>
                    </Box>
                  );
                })}
                {roleItems.length > 6 && (
                  <Typography sx={{ fontSize: 9, color: "#94a3b8", mt: "2px" }}>
                    +{roleItems.length - 6} more
                  </Typography>
                )}
              </Box>
            )}
          </Stack>

        </Stack>
      </Box>
    </Box>
  );
  
}


export async function loadDemographics(constCode) {
  const res = await fetch("/api/fetch_ext_data/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      geoLevel: "constituency",
      constituencyCodes: [constCode],
    }),
  });

  const json = await res.json();
  console.log("EXT DEMO RAW RESPONSE:", json);

  if (Array.isArray(json)) return json[0];
  if (json?.rows && json.rows.length) return json.rows[0];
  return null;
}

export const NationalBaseline = {
  over_50: 0.478,
  deprived_share: 0.524,
  white_share: 0.829,
  homeowners_share: 0.616,
  english_share: 0.982,
};




function buildBlurb(demo, constName) {
  if (!demo) return "";

  const metrics = [
    {
      key: "over_50",
      phraseHigh: "older",
      phraseLow: "younger",
      val: demo.over_50,
      nat: NationalBaseline.over_50,
    },
    {
      key: "deprived_share",
      phraseHigh: "more deprived",
      phraseLow: "less deprived",
      val: demo.deprived_share,
      nat: NationalBaseline.deprived_share,
    },
    {
      key: "white_share",
      phraseHigh: "more white",
      phraseLow: "more diverse",
      val: demo.white_share,
      nat: NationalBaseline.white_share,
    },
    {
      key: "homeowners_share",
      phraseHigh: "more likely to own their home",
      phraseLow: "less likely to own their home",
      val: demo.homeowners_share,
      nat: NationalBaseline.homeowners_share,
    },
    {
      key: "english_share",
      phraseHigh: "more English-speaking",
      phraseLow: "more linguistically diverse",
      val: demo.english_share,
      nat: NationalBaseline.english_share,
    },
  ];

  const phrases = metrics
    .map((m) => {
      if (m.val == null || m.nat == null) return null;
      const delta = (m.val - m.nat) * 100;
      if (Math.abs(delta) < 1.0) return null;
      return delta > 0 ? m.phraseHigh : m.phraseLow;
    })
    .filter(Boolean);

  if (!phrases.length) {
    return `${constName} is broadly in line with the country as a whole.`;
  }

  const topTwo = phrases.slice(0, 4);
  const text = topTwo.join(", ").replace(/,([^,]*)$/, " and$1");

  return `${constName} is a bit ${text} than the country as a whole.`;
}




/** Tiny gauge: filled bar = constituency; thin tick = national baseline */
function MiniGauge({ val = 0, nat = 0 }) {
  const v = Math.max(0, Math.min(100, Math.round(val * 100)));
  const n = Math.max(0, Math.min(100, Math.round(nat * 100)));

  return (
    <Box
      aria-label={`Constituency ${v} percent vs national ${n} percent`}
      sx={{
        position: "relative",
        height: 4,
        borderRadius: 999,
        bgcolor: "#e2e8f0",
        overflow: "visible",
        mt: "2px",
      }}
    >
      {/* Constituency fill */}
      <Box sx={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: `${v}%`,
        bgcolor: "#0abfbc",
        borderRadius: 999,
        opacity: 0.75,
      }} />
      {/* National tick */}
      <Box sx={{
        position: "absolute",
        left: `calc(${n}% - 1px)`,
        top: -2,
        bottom: -2,
        width: "2px",
        backgroundColor: "#0f172a",
        opacity: 0.4,
        borderRadius: 1,
      }} />
    </Box>
  );
}

export function Field({ label, val, nat, icon, hideComparison = false }) {
  // National reports have no "vs national" comparison (the value IS national).
  const hasData = hideComparison ? val != null : (val != null && nat != null);
  const valPct = val != null ? (val * 100).toFixed(0) : "—";
  const natPct = nat != null ? (nat * 100).toFixed(0) : "—";
  const delta = (val != null && nat != null) ? (val - nat) * 100 : null;

  const showDelta = !hideComparison && delta !== null && Math.abs(delta) >= 0.5;
  const isUp = showDelta && delta > 0;

  return (
    <Box sx={{
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: "5px",
      px: "8px",
      py: "9px",
      borderRadius: "3px",
      bgcolor: "#f8fafc",
      border: "0.5px solid #e2e8f0",
    }}>
      {/* Icon + label row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
        <Box sx={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          backgroundColor: icon?.color || "#0abfbc",
          color: "white",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}>
          {icon?.el}
        </Box>
        <Typography sx={{
          fontSize: 8.5,
          fontWeight: 700,
          color: "#475569",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          letterSpacing: "0.01em",
        }} title={label}>
          {label}
        </Typography>
      </Box>

      {/* Values row */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "3px", flexWrap: "nowrap" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
          {hasData ? `${valPct}%` : "—"}
        </Typography>
        {hasData && !hideComparison && (
          <Typography sx={{ fontSize: 9, color: "#64748b", fontWeight: 500 }} title={`National: ${natPct}%`}>
            ({natPct}%)
          </Typography>
        )}
        {showDelta && (
          <Typography component="span" sx={{
            ml: "auto",
            px: "4px",
            py: "1px",
            borderRadius: "3px",
            fontSize: 8.5,
            fontWeight: 700,
            lineHeight: 1.4,
            color: isUp ? "#15803d" : "#b91c1c",
            bgcolor: isUp ? "#dcfce7" : "#fee2e2",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}>
            {(delta > 0 ? "+" : "") + delta.toFixed(1)} pp
          </Typography>
        )}
      </Box>

      {hasData && !hideComparison && <MiniGauge val={val} nat={nat} />}
    </Box>
  );
}





export function DemographicBlock({ constCode, constName, showFields = true, variant = "full", national = false }) {
  const [demo, setDemo] = useState(national ? NationalBaseline : null);
  const [loading, setLoading] = useState(!national);

  useEffect(() => {
    if (national || !constCode) return;
    setLoading(true);

    loadDemographics(constCode)
      .then((d) => setDemo(d))
      .catch(() => setDemo(null))
      .finally(() => setLoading(false));
  }, [constCode, national]);

  if (loading) {
    return (
      <Box
        sx={{
          height: 160,
          border: "1px dashed #d0d0d0",
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.disabled",
          fontSize: "0.9rem",
        }}
      >
        Loading demographic profile…
      </Box>
    );
  }

  if (!demo) {
    return (
      <Box
        sx={{
          height: 160,
          border: "1px dashed #f44336",
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "error.main",
          textAlign: "center",
          px: 2,
          fontSize: "0.9rem",
        }}
      >
        No demographic data returned for this constituency.
      </Box>
    );
  }

  const blurb = national
    ? "Population shares across Great Britain (ONS baseline)."
    : buildBlurb(demo, constName);

  if (variant === "blurb") {
    return (
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          lineHeight: 1.35,
          fontSize: "0.85rem",
        }}
      >
        {blurb}
      </Typography>
    );
  }

  return (
<Box sx={{
  py: "12px",
  borderTop: "0.5px solid #e2e8f0",
  borderBottom: "0.5px solid #e2e8f0",
}}>
  {/* Header row */}
  <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px", mb: "10px" }}>
    <Typography sx={{
      fontSize: 9,
      fontWeight: 800,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "#0abfbc",
      flexShrink: 0,
      whiteSpace: "nowrap",
    }}>
      Demographic Profile
    </Typography>
    <Box sx={{ width: "2px", height: "10px", bgcolor: "#e2e8f0", flexShrink: 0 }} />
    <Typography sx={{
      fontSize: 9,
      color: "#475569",
      lineHeight: 1.3,
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: 1,
      WebkitBoxOrient: "vertical",
      whiteSpace: "normal",
      flex: 1,
      minWidth: 0,
    }} title={blurb}>
      {blurb}
    </Typography>
  </Box>

  {showFields && (
    <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: "5px" }}>
      <Field label="Over 50" val={demo.over_50} nat={NationalBaseline.over_50} hideComparison={national} icon={{ el: <ElderlyRoundedIcon sx={{ fontSize: 10 }} />, color: purple[500] }} />
      <Field label="Deprived" val={demo.deprived_share} nat={NationalBaseline.deprived_share} hideComparison={national} icon={{ el: <FmdBadRoundedIcon sx={{ fontSize: 10 }} />, color: green[700] }} />
      <Field label="White" val={demo.white_share} nat={NationalBaseline.white_share} hideComparison={national} icon={{ el: <PeopleIcon sx={{ fontSize: 10 }} />, color: blue[700] }} />
      <Field label="Homeowners" val={demo.homeowners_share} nat={NationalBaseline.homeowners_share} hideComparison={national} icon={{ el: <HomeIcon sx={{ fontSize: 10 }} />, color: grey[600] }} />
      <Field label="English speakers" val={demo.english_share} nat={NationalBaseline.english_share} hideComparison={national} icon={{ el: <TranslateRoundedIcon sx={{ fontSize: 10 }} />, color: amber[700] }} />
    </Box>
  )}
</Box>
  );
}